// src/components/VideoForm/index.js
import React, { useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  FormLabel,
  IconButton,
  Image,
  Input,
  Select,
  Skeleton,
  Stack,
  useToast,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { v4 } from "uuid";
import { MdAddAPhoto } from "react-icons/md";
import client from "../../utils/client";
import { useNavigate, useParams } from "react-router-dom";
import { getImageUploadUrl } from "../../utils/s3";
import axios from "axios";

const VideoForm = () => {
  const { videoId } = useParams();
  const [video, setVideo] = useState(null);
  const { register, handleSubmit } = useForm();
  const toast = useToast();
  const navigate = useNavigate();
  const fileInput = useRef(null);

  // Holds preview URL (existing S3 URL or local blob)
  const [previewImage, setPreviewImage] = useState(null);
  // Holds the File object selected by the user
  const [selectedFile, setSelectedFile] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  // Load existing video when editing
  useEffect(() => {
    if (!videoId) return;
    setIsLoading(true);
    client
      .get(`/videos/${videoId}`)
      .then((res) => {
        setVideo(res.data);
        // set initial preview to first image from S3
        if (res.data.images?.[0]) {
          setPreviewImage(
            `https://${process.env.REACT_APP_S3_BUCKET}.s3.${process.env.REACT_APP_S3_REGION}.amazonaws.com/videos_images/${res.data.images[0]}`
          );
        }
      })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, [videoId]);

  // Handle file selection
  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setPreviewImage(URL.createObjectURL(file));
  };

  // Shared submit handler
  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      // Determine imageKey: existing or newly uploaded
      let imageKey = video?.image || null;

      if (selectedFile) {
        // Generate unique filename & S3 key
        const filename = `videoImage-${v4()}.jpeg`;
        const key = `videos_images/${filename}`;

        // 1) get signed URL
        const signedUrl = await getImageUploadUrl(key, selectedFile.type);

        // 2) upload to S3
        await axios.put(signedUrl, selectedFile, {
          headers: { "Content-Type": selectedFile.type },
        });

        imageKey = filename;
      }

      // Build tags array
      const tags = data.tagsUnited.split(",").map((t) => t.trim());

      // Payload for create or update
      const payload = {
        image: imageKey,
        name: data.name,
        url: data.url,
        eventName: data.eventName,
        authorName: data.authorName,
        gradeRestriction: data.gradeRestriction,
        tags,
      };

      if (video) {
        await client.put(`/videos/${video.videoId}`, payload);
        toast({
          title: "Vídeo alterado com sucesso",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        await client.post("/videos", payload);
        toast({
          title: "Vídeo cadastrado com sucesso",
          description: "O vídeo foi cadastrado!",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }

      navigate("/allVideos");
    } catch (err) {
      console.error(err);
      toast({
        title: "Algum erro aconteceu",
        description: "Contate o Administrador do Sistema",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setIsLoading(false);
    }
  };

  return (
    <Skeleton isLoaded={!isLoading}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex flexDir="column" gap="5" my={5}>
          {/* Vídeo Info */}
          <Box border="1px solid #ccc" borderRadius="8px" p={[2, 5]} bg="white">
            <Badge fontSize="1rem" colorScheme="green" mb={2}>
              Informações do Vídeo
            </Badge>
            <Flex flexDir="column" gap={3}>
              <Stack direction={["column", "row"]}>
                <FormLabel w="100%" m={0}>
                  Título do Vídeo
                  <Input
                    required
                    type="text"
                    placeholder="Nome do vídeo"
                    {...register("name")}
                    defaultValue={video?.name}
                  />
                </FormLabel>
              </Stack>
              <Stack direction={["column", "row"]}>
                <FormLabel w="100%" m={0}>
                  Link de destino
                  <Input
                    required
                    type="text"
                    placeholder="http://..."
                    {...register("url")}
                    defaultValue={video?.url}
                  />
                </FormLabel>
              </Stack>
              <Stack direction={["column", "row"]}>
                <FormLabel w="100%" m={0}>
                  Nome do Evento
                  <Input
                    required
                    type="text"
                    {...register("eventName")}
                    defaultValue={video?.eventName}
                  />
                </FormLabel>
                <FormLabel w="100%" m={0}>
                  Restrição por Grau
                  <Select
                    {...register("gradeRestriction")}
                    defaultValue={video?.gradeRestriction || "1"}
                  >
                    <option value="1">Sem restrições</option>
                    <option value="2">Para Companheiros</option>
                    <option value="3">Para Mestres</option>
                  </Select>
                </FormLabel>
              </Stack>
              <Stack direction={["column", "row"]}>
                <FormLabel w="100%" m={0}>
                  Tags (Separe por vírgula)
                  <Input
                    required
                    type="text"
                    placeholder="história, filosofia..."
                    {...register("tagsUnited")}
                    defaultValue={video?.tags?.join(", ") || ""}
                  />
                </FormLabel>
                <FormLabel w="100%" m={0}>
                  Autor do Vídeo
                  <Input
                    type="text"
                    {...register("authorName")}
                    defaultValue={video?.authorName}
                  />
                </FormLabel>
              </Stack>
            </Flex>
          </Box>

          {/* Upload de Imagem */}
          <Box p={[2, 5]} bg="white" border="1px solid #ccc" borderRadius="8px">
            <Badge fontSize="1rem" colorScheme="green" mb={2}>
              Imagem do vídeo para o Card
            </Badge>
            <Flex
              flexDir={["column", "row"]}
              alignItems="center"
              justifyContent="space-between"
              gap={5}
            >
              <div>
                <Input
                  type="file"
                  accept="image/*"
                  id="fileInput"
                  display="none"
                  onChange={handleImage}
                  ref={fileInput}
                />
                <label htmlFor="fileInput">
                  <Flex alignItems="center" gap={10}>
                    <FormLabel m={0}>Adicione a imagem do Vídeo</FormLabel>
                    <IconButton
                      colorScheme="green"
                      borderRadius="100%"
                      w={["70px", "85px"]}
                      h={["70px", "85px"]}
                      aria-label="Upload File"
                      icon={<MdAddAPhoto fontSize="45px" />}
                      onClick={() => fileInput.current.click()}
                    />
                  </Flex>
                </label>
              </div>
              <Flex justifyContent="center" w="100%">
                {previewImage && <Image objectFit="cover" src={previewImage} />}
              </Flex>
            </Flex>
          </Box>

          {/* Botão de Envio */}
          <Flex justifyContent="center">
            <Button
              isLoading={isLoading}
              colorScheme="green"
              w={["100%", "40%"]}
              type="submit"
            >
              {video ? "Editar Vídeo" : "Cadastrar Vídeo"}
            </Button>
          </Flex>
        </Flex>
      </form>
    </Skeleton>
  );
};

export default VideoForm;
